import { Component, Inject } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { updateCurrencyAmount } from '../core/components/currency-input-calculations';
import { NumpadValue } from '../core/components/numpad.component';
import { ScreenLayout, SCREEN_LAYOUT } from '../core/layout/layout.provider';
import { DataService } from '../data/data.service';
import { CashBoxTransactionType, DepositMoney, WithdrawMoney } from '../data/models';

@Component({
    selector: 'app-deposit-withdraw',
    styles: [`
    :host {
      display: block;
      height: 100%;
      width: 100%;
    }

    div.is-invalid {
      border-color: var(--bs-danger);
    }

    .side-buttons {
      margin-left: -115px;
      margin-right: -115px;
    }

    .hstack {
      padding: 1rem 0.5rem;
      padding-right: 0.75rem;
    }
  `],
    template: `
    <div class="modal-header">
      <h4 class="modal-title" id="exampleModalLabel" i18n>Bargeld</h4>
      <button type="button" class="btn-close" aria-label="Close" i18n-aria-label
              (click)="modalService.close()"></button>
    </div>
    <div class="modal-body d-flex p-0">
      <div class="d-flex flex-column flex-lg-row w-100">
        <div class="d-flex flex-column border-end border-medium col-12 col-lg-6">
          <div class="d-flex d-lg-none justify-content-center">
            <div class="btn-group btn-group-lg pt-3 w-100 px-3" role="group">
              <input type="radio" class="btn-check" id="deposit-secondary"
                     [formControl]="type"
                     autocomplete="off" [value]="Types.MoneyTransit">
              <label class="btn btn-outline-info" for="deposit-secondary">Geldtransit</label>

              <input type="radio" class="btn-check" id="withdraw-secondary"
                     [formControl]="type"
                     autocomplete="off" [value]="Types.PrivateWithdrawal">
              <label class="btn btn-outline-info" for="withdraw-secondary">{{ getLabelForWithdraw() }}</label>
            </div>
          </div>

          <div class="d-flex flex-column align-items-center p-3">
            <div class="input-group focus-input-group">
              <div class="input-group-text bg-white"
                   [class.border-danger]="mode==='expense'"
                   [class.border-success]="mode==='revenue'"
                   [class.is-invalid]="price === 0">
                <button type="button" class="btn-close" (click)="revertAmount()"></button>
              </div>
              <app-currency-input class="form-control fs-4 form-control-lg border-start-0"
                                  ngbAutofocus
                                  [value]="price"
                                  [class.is-invalid]="price === 0"
                                  [class.border-danger]="mode==='expense'"
                                  [class.border-success]="mode==='revenue'"
                                  (valueChange)="updateAmount($event)"></app-currency-input>
            </div>
          </div>
          <div class="hstack justify-content-center">
            <div class="d-flex flex-column">
              <div class="rotate side-buttons pe-3">
                <button class="btn btn-lg mx-3"
                        [class.btn-danger]="mode==='expense'"
                        [class.btn-outline-danger]="mode==='revenue'"
                        [class.bg-medium]="mode==='revenue'"
                        (click)="setMode('expense')" i18n>Auszahlung
                </button>
                <button class="btn btn-lg"
                        [class.btn-success]="mode==='revenue'"
                        [class.text-white]="mode==='revenue'"
                        [class.btn-outline-success]="mode==='expense'"
                        [class.bg-medium]="mode==='expense'"
                        (click)="setMode('revenue')" i18n>Einzahlung
                </button>
              </div>
            </div>
            <div class="d-flex flex-grow-1 flex-shrink-1"></div>
            <app-numpad theme="dark" [showDecimalZeroes]="true" (buttonPress)="handleNumpadEvent($event)"></app-numpad>
          </div>
        </div>
        <div class="vstack p-3">
          <div class="d-none d-lg-flex justify-content-center">
            <div class="btn-group btn-group-lg pb-3 w-100" role="group">
              <input type="radio" class="btn-check" id="deposit"
                     [formControl]="type"
                     autocomplete="off" [value]="Types.MoneyTransit">
              <label class="btn btn-outline-info" for="deposit">Geldtransit</label>

              <input type="radio" class="btn-check" id="withdraw"
                     [formControl]="type"
                     autocomplete="off" [value]="Types.PrivateWithdrawal">
              <label class="btn btn-outline-info" for="withdraw">{{ getLabelForWithdraw() }}</label>
            </div>
          </div>

          <div class="d-flex justify-content-between align-items-center flex-nowrap">
            <span class="fs-5 text-muted" *ngIf="mode==='expense'" i18n>Auszahlung</span>
            <span class="fs-5 text-muted" *ngIf="mode==='revenue'" i18n>Einzahlung</span>
            <span class="fs-5">{{price | currency}}</span>
          </div>

          <div class="d-flex justify-content-between align-items-center">
            <span class="fs-5 text-muted" i18n>Aktueller Bestand</span>
            <span class="fs-5">{{dataService.cashBox.cashBalance| currency}}</span>
          </div>

          <div class="d-flex justify-content-between align-items-center py-3">
            <span class="fs-5 text-muted" i18n>Neuer Bestand</span>
            <span class="fs-3"
                  [class.text-success]="mode==='revenue'"
                  [class.text-danger]="mode==='expense'">
              {{differenceAmount | currency}}
            </span>
          </div>

          <div class="d-flex flex-grow-1 flex-shrink-1"></div>

          <button type="button" class="btn btn-lg text-white w-100 py-3"
                  [class.btn-success]="mode==='revenue'"
                  [class.btn-danger]="mode==='expense'"
                  (click)="processAction()"
                  [disabled]="loading"
                  [appLoadingOverlay]="loading"
                  i18n>
            Buchung durchführen
          </button>
        </div>
      </div>
    </div>
  `,
    standalone: false
})
export class DepositWithdrawPage {
  loading = false;

  price: number = 0;
  Types = {
    MoneyTransit: 'transit',
    PrivateWithdrawal: 'private'
  };
  mode: CashBoxTransactionType = 'revenue';
  type: UntypedFormControl = new UntypedFormControl(this.Types.MoneyTransit, [Validators.required]);

  constructor(@Inject(SCREEN_LAYOUT) public layout: ScreenLayout,
    public dataService: DataService,
    public readonly modalService: NgbActiveModal
  ) { }

  get differenceAmount(): number {
    if (this.mode === 'revenue') {
      return this.price + this.dataService.cashBox.cashBalance;
    }
    return this.dataService.cashBox.cashBalance - this.price;
  }

  updateAmount(value: number) {
    this.price = value;
  }

  handleNumpadEvent(value: NumpadValue) {
    const updatedAmount = updateCurrencyAmount(this.price, value);
    this.updateAmount(updatedAmount);
  }

  revertAmount() {
    this.price = 0;
  }

  getPayload() {
    if (this.mode === 'revenue') {
      return {
        amount: this.price,
        type: 'revenue',
        category: this.type.value === this.Types.MoneyTransit ? 'revenue_money_transfer' : 'revenue_private'
      } as DepositMoney;
    }

    return {
      amount: -this.price,
      type: 'expense',
      category: this.type.value === this.Types.MoneyTransit ? 'expense_money_transfer' : 'expense_private'
    } as WithdrawMoney;
  }

  async processAction() {
    this.loading = true;

    try {
      const action = this.getPayload();
      await this.dataService.cashBoxTransferAction(action);
    } finally {
      this.loading = false;
    }

    this.modalService.close();
  }

  setMode(mode: CashBoxTransactionType = 'revenue') {
    this.mode = mode;
  }

  getLabelForWithdraw(): string {
    return this.mode === 'revenue' ? 'Privateinlage' : 'Privatentnahme';
  }
}
